import { useTranslation } from "react-i18next";
import { IAutomationReceivedSlotProps } from "./models";
import {
  AutoComplete,
  AutoCompleteMulti,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import {
  automationReceivedSlotSchema,
  TAutomationReceivedSlotForm,
} from "./automationReceivedSlotSchema";
import {
  createTicketAutomationReceivedSlot,
  getTicketAutomationReceivedSlotById,
  updateTicketAutomationReceivedSlotById,
} from "../../../../../../../services/automationReceivedSlot.service";
import { listMainGenerics } from "../../../../../../../services/generic.service";
import { calendarRegisterList } from "../../../../../../../services/ticketAutomationReceivedSlotCalendar.service";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedSlot.";

const AutomationReceivedSlot = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
  automationCode,
}: IAutomationReceivedSlotProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedSlot,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedSlotById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedSlotById,
  );

  const updateHandler = (v: TAutomationReceivedSlotForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        calendarSlotSortTypeId: v.calendarSlotSortType.id,
        slotsAvailable: Number(v.slotsAvailable),
        priority: Number(v.priority),
        enable: v.enable,
        ticketAutomationReceivedSlotCalendars:
          v.ticketAutomationReceivedSlotCalendars
            ? v.ticketAutomationReceivedSlotCalendars.map((cal) => ({
                id: cal.id,
                calendarExternalId: cal.id,
                calendarExternalName: cal.name,
              }))
            : undefined,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TAutomationReceivedSlotForm) =>
    save({
      ...v,

      calendarSlotSortTypeId: v.calendarSlotSortType.id,
      slotsAvailable: Number(v.slotsAvailable),
      priority: Number(v.priority),
      enable: v.enable,
      ticketAutomationReceivedSlotCalendars:
        v.ticketAutomationReceivedSlotCalendars
          ? v.ticketAutomationReceivedSlotCalendars.map((cal) => ({
              id: cal.id,
              calendarExternalId: cal.id,
              calendarExternalName: cal.name,
            }))
          : undefined,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TAutomationReceivedSlotForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TAutomationReceivedSlotForm = {
          ...data,
          calendarSlotSortType: {
            id: data.calendarSlotSortType.id,
            name: data.calendarSlotSortType.name,
          },
          slotsAvailable: data.slotsAvailable.toString(),
          priority: data.priority.toString(),
          enable: data.enable,
          ticketAutomationReceivedSlotCalendars:
            data.ticketAutomationReceivedSlotCalendars
              ? data.ticketAutomationReceivedSlotCalendars.map((cal) => ({
                  id: cal.calendarExternalId,
                  name: cal.calendarExternalName,
                }))
              : undefined,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const [
    optionsTicketAutomationReceivedSlotCalendars,
    setTicketAutomationReceivedSlotCalendars,
  ] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(calendarRegisterList);

  const tasyScheduleTypeCode = useMemo(() => {
    if (!automationCode) return;

    switch (automationCode) {
      case "20":
      case "25":
      case "26":
        return [3];
      case "21":
        return [2];
      default:
        return null;
    }
  }, [automationCode]);
  const classificationConsultationIdentifier = useMemo(() => {
    if (!automationCode) return;

    switch (automationCode) {
      case "25":
        return ["R"];
      default:
        return null;
    }
  }, [automationCode]);

  const params = useMemo(() => {
    return {
      EnableCode: true,
      TasyScheduleTypeCode: tasyScheduleTypeCode,
      ClassificationConsultationIdentifier:
        classificationConsultationIdentifier,
    };
  }, [tasyScheduleTypeCode]);

  const fetchTicketAutomationReceivedSlotCalendars = useCallback(async () => {
    const { data, success } = await sendRequest(params);

    if (data && success) {
      const isType3Selected =
        Array.isArray(tasyScheduleTypeCode) && tasyScheduleTypeCode.includes(3);

      setTicketAutomationReceivedSlotCalendars(
        data.map((d) => {
          const descriptionSite = d.descriptionSite?.trim();
          const calendarShortName = d.calendarShortName?.trim();
          const calendarName = d.calendarName?.trim();
          const displayName =
            [descriptionSite, calendarShortName, calendarName].find(Boolean) ||
            d.identifier.toString();
          const titleBase = isType3Selected
            ? displayName
            : calendarShortName || calendarName || d.identifier.toString();

          return {
            id: String(d.identifier),
            name: `${titleBase} - ${d.identifier}`,
          };
        }),
      );
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchTicketAutomationReceivedSlotCalendars();
  }, [fetchTicketAutomationReceivedSlotCalendars]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TAutomationReceivedSlotForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "slot_schedule")}
      schema={automationReceivedSlotSchema}
      subtitle={t(translationPath + "new_slot_schedule")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item sm={12} textAlign={"end"}>
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>
          <Grid item sm={12}>
            <Typography fontWeight="bold">
              {t(translationPath + "if")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <AutoCompleteMulti
              params={params}
              fullWidth
              options={optionsTicketAutomationReceivedSlotCalendars}
              name="ticketAutomationReceivedSlotCalendars"
              label={t(
                translationPath + "ticketAutomationReceivedSlotCalendars",
              )}
              getOptionLabel={(option) => option.name}
              size="small"
              keepOpenOnSelect={true}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormInput
              type="number"
              size="small"
              name="priority"
              fullWidth
              label={t(translationPath + "priority") || "Priority"}
            />
          </Grid>
          <Grid item sm={12}>
            <Typography fontWeight="bold">
              {t(translationPath + "then")}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5}>
            <FormInput
              type="number"
              size="small"
              name="slotsAvailable"
              fullWidth
              label={
                t(translationPath + "slots_available") || "Slots Available"
              }
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "calendar_slot_sort_type")}
              name="calendarSlotSortType"
              request={listMainGenerics}
              params={{ Identifier: "CalendarSlotSortType" }}
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AutomationReceivedSlot;
